<template>
  <modal
    :id="modalId"
    ref="modal"
    class="add-actor-to-community"
    wrapper-size="large"
    :title="modalTitle"
    is-simplified-modal
    @close="close"
  >
    <template v-slot:body>
      <div class="add-actor-to-community__body">
        <p class="add-actor-to-community__intro">
          {{ $t('community_add_actor_subtitle', { actor: actorsLabel }) }}
        </p>

        <!-- Stays in view while the results underneath scroll -->
        <div class="add-actor-to-community__search">
          <DsInput
            v-model="query"
            icon="search"
            is-simplified
            autofocus
            :placeholder="$t('community_add_actor_search_placeholder')"
          />

          <div v-if="selected.length" class="tw-flex flex-wrap gap-2 mt-3">
            <span
              v-for="actor in selected"
              :key="actor.elastic_id"
              class="add-actor-to-community__chip tw-flex items-center gap-1"
            >
              <span class="truncate">{{ actor.name }}</span>
              <button
                type="button"
                class="add-actor-to-community__chip-remove"
                :aria-label="$t('community_add_actor_deselect')"
                @click="toggle(actor)"
              >×</button>
            </span>
          </div>
        </div>

        <div class="add-actor-to-community__results">
          <div v-if="loading" class="tw-flex items-center justify-center py-10">
            <Loading/>
          </div>

          <div
            v-else-if="!results.length"
            class="add-actor-to-community__empty text-center"
          >
            <p class="text-sm font-semibold text-gray-700 mb-1">
              {{ query ? $t('community_add_actor_no_matches_title') : $t('community_add_actor_no_results_title') }}
            </p>
            <p class="text-sm text-gray-400 m-0">
              {{ query ? $t('community_add_actor_no_results_sub') : $t('community_add_actor_all_added', { actor: actorsLabel }) }}
            </p>
          </div>

          <template v-else>
            <p v-if="!query" class="add-actor-to-community__section-label">
              {{ $t('community_add_actor_recent') }}
            </p>

            <ul class="add-actor-to-community__list tw-flex flex-col gap-2">
              <li v-for="actor in results" :key="actor.elastic_id">
                <button
                  type="button"
                  class="add-actor-to-community__row tw-flex items-center gap-3 w-full text-left"
                  :class="{
                    'add-actor-to-community__row--selected': isSelected(actor),
                    'add-actor-to-community__row--added': isAlreadyAdded(actor),
                  }"
                  :disabled="isAlreadyAdded(actor)"
                  @click="toggle(actor)"
                >
                  <span class="add-actor-to-community__logo shrink-0" :style="logoStyle(actor)">
                    <span v-if="!logoUrl(actor)">{{ initials(actor.name) }}</span>
                  </span>

                  <span class="tw-flex flex-col flex-1 min-w-0">
                    <span class="text-sm font-semibold text-gray-900 truncate">{{ actor.name }}</span>
                    <span class="text-xs text-gray-500 truncate">
                      {{ subtitleFor(actor) }}
                    </span>
                  </span>

                  <span v-if="isAlreadyAdded(actor)" class="add-actor-to-community__badge shrink-0">
                    {{ $t('community_add_actor_already_added') }}
                  </span>

                  <span
                    v-else
                    class="add-actor-to-community__check shrink-0"
                    :class="{ 'add-actor-to-community__check--on': isSelected(actor) }"
                  >
                    <Icon v-if="isSelected(actor)" name="check" size="12"/>
                  </span>
                </button>
              </li>
            </ul>
          </template>

          <p v-if="!loading && query && hasMoreResults" class="add-actor-to-community__hint">
            {{ $t('community_add_actor_refine_search', { count: total }) }}
          </p>

          <p v-if="error" class="add-actor-to-community__error">{{ error }}</p>
        </div>

        <!-- Escape hatch for an actor that genuinely does not exist yet in the ecosystem -->
        <div class="add-actor-to-community__create-row">
          <button type="button" class="add-actor-to-community__create" @click="createActor">
            {{ $t('community_add_actor_create_instead', { actor: actorLabel }) }}
          </button>
        </div>
      </div>
    </template>

    <template v-slot:footer>
      <DsButton
        variant="rounded"
        size="extra-small"
        :label="submitLabel"
        :disabled="!selected.length || submitting"
        @click="submit"
      />
    </template>
  </modal>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import _debounce from 'lodash/debounce'

  import Modal from './Modal.vue'
  import DsInput from '../Form/DsInput.vue'
  import DsButton from '../DsButton/DsButton.vue'
  import Icon from '../Icon/Icon.vue'
  import Loading from '../Dashboard/ConceptMap/Loading.vue'

  import MODAL_IDS from '../../constants/modal-ids.js'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '../../store/modules/ui.js'
  import { addActorsToCommunity, searchAddableCommunityActors } from '../../api/communities'
  import { actorLogo } from '../../util/actor'
  import { trackMatomoEvent } from '../../util/analytics'
  import { MATOMO_EVENT_ACTIONS, MATOMO_EVENT_CATEGORIES } from '../../constants/analytics-constants.js'
  import TranslationsMixin from '../../util/TranslationsMixin.js'

  export default defineComponent({
    name: 'AddActorToCommunityModal',
    components: { Modal, DsInput, DsButton, Icon, Loading },
    mixins: [TranslationsMixin],
    data() {
      return {
        modalId: MODAL_IDS.ADD_ACTOR_TO_COMMUNITY_MODAL,
        query: '',
        results: [] as any[],
        selected: [] as any[],
        total: 0,
        loading: false,
        submitting: false,
        error: '',
      }
    },
    computed: {
      modalContext(): Record<string, any> {
        return this.$store.state.ui.modalContext || {}
      },
      communityId(): number | string | null {
        return this.modalContext.communityId || null
      },
      communityName(): string {
        return this.modalContext.communityName || ''
      },
      portfolioId(): number | string | null {
        return this.modalContext.portfolioId || null
      },
      modalTitle(): string {
        return this.$t('community_add_actor_title', { actor: this.actorsLabel })
      },
      // The search is capped server side, so a manager searching a large ecosystem is nudged
      // towards a narrower query instead of scrolling an endless list
      hasMoreResults(): boolean {
        return this.total > this.results.length
      },
      submitLabel(): string {
        if (this.submitting) {
          return this.$t('community_add_actor_adding')
        }

        if (this.selected.length > 1) {
          return this.$t('community_add_actor_submit_multiple', { count: this.selected.length })
        }

        return this.$t('community_add_actor_submit', { actor: this.actorLabel })
      },
    },
    methods: {
      initials(name: string): string {
        if (!name) return '?'
        return name.split(' ').slice(0, 2).map((part: string) => part[0]).join('').toUpperCase()
      },
      logoUrl(actor: any): string | null {
        return actorLogo(actor) || null
      },
      logoStyle(actor: any): Record<string, string> {
        const logo = this.logoUrl(actor)

        return logo ? { backgroundImage: `url(${logo})` } : {}
      },
      subtitleFor(actor: any): string {
        const location = [actor.city, actor.country].filter(Boolean).join(', ')

        return [location, actor.url].filter(Boolean).join(' · ') || actor.actor_type
      },
      isSelected(actor: any): boolean {
        return this.selected.some((a: any) => a.elastic_id === actor.elastic_id)
      },
      isAlreadyAdded(actor: any): boolean {
        return !!actor.is_in_community
      },
      toggle(actor: any) {
        if (this.isAlreadyAdded(actor)) {
          return
        }

        this.error = ''

        if (this.isSelected(actor)) {
          this.selected = this.selected.filter((a: any) => a.elastic_id !== actor.elastic_id)

          return
        }

        this.selected = [...this.selected, actor]
      },
      fetch() {
        if (!this.communityId) {
          return
        }

        const query = this.query.trim()

        this.loading = true

        // Without a query the server returns the most recent additions to the ecosystem: a short
        // peek at likely candidates. A real search may need a few more rows to hold the match.
        searchAddableCommunityActors(this.communityId, { q: query || null, limit: query ? 10 : 5 })
          .then((response: any) => {
            this.results = response.data
            this.total = response.total
          })
          .catch(() => {
            this.results = []
            this.total = 0
          })
          .finally(() => {
            this.loading = false
          })
      },
      submit() {
        if (!this.selected.length || this.submitting) {
          return
        }

        this.submitting = true
        this.error = ''

        const actorIds = this.selected.map((actor: any) => actor.elastic_id)

        addActorsToCommunity(this.communityId, actorIds)
          .then((response: any) => {
            trackMatomoEvent(
              MATOMO_EVENT_CATEGORIES.COMMUNITY_MANAGEMENT,
              MATOMO_EVENT_ACTIONS.ADD_ACTOR_TO_COMMUNITY,
              `{communityId: ${this.communityId},count: ${(response.added || actorIds).length}}`,
            )

            // Elasticsearch needs a moment to reflect the change, so the actor views are
            // refreshed through the same events the actor pages already listen to
            this.$bus.emit('communityActorsAdded', response.added || actorIds)
            this.$bus.emit('actorUpdated')

            this.close()
          })
          .catch(() => {
            this.error = this.$t('community_add_actor_error')
          })
          .finally(() => {
            this.submitting = false
          })
      },
      createActor() {
        this.$store.commit(UI_MUTATION_TYPES.HIDE_MODAL, this.modalId)
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, {
          portfolioId: this.portfolioId,
        })
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.ADD_ACTOR)
      },
      close() {
        this.$store.commit(UI_MUTATION_TYPES.HIDE_MODAL, this.modalId)
      },
    },
    mounted() {
      this.fetch()
    },
    watch: {
      query: _debounce(function (this: any) {
        this.fetch()
      }, 300),
    },
  })
</script>

<style lang="scss" scoped>
  // The simplified modal strips all padding from its container and scrolls the body itself, so the
  // spacing of the sections below is what gives the modal its gutters.
  // Free to wrap onto as many lines as it needs, unlike the fixed-height modal header
  .add-actor-to-community__intro {
    margin: 0;
    padding: 16px 20px 0;
    font-size: 0.8rem;
    line-height: 1.4;
    color: #6b7280;
  }

  .add-actor-to-community__search {
    position: sticky;
    top: 0;
    z-index: 2;
    padding: 12px 20px 14px;
    background: white;
    border-bottom: 1px solid #f3f4f6;
  }

  .add-actor-to-community__results {
    padding: 14px 20px 0;
    min-height: 150px;
  }

  .add-actor-to-community__list {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .add-actor-to-community__empty {
    padding: 32px 20px;
    border: 1px dashed #e5e7eb;
    border-radius: 8px;
    background: #f9fafb;
  }

  .add-actor-to-community__section-label {
    margin: 0 0 8px;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #9ca3af;
  }

  .add-actor-to-community__hint {
    margin: 12px 0 0;
    font-size: 0.7rem;
    color: #9ca3af;
    text-align: center;
  }

  .add-actor-to-community__error {
    margin: 12px 0 0;
    font-size: 0.8rem;
    color: #dc2626;
  }

  .add-actor-to-community__create-row {
    padding: 14px 20px 18px;
  }

  .add-actor-to-community__row {
    padding: 10px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: white;
    cursor: pointer;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out, background-color 0.15s ease-in-out;

    &:hover {
      border-color: #d1d5db;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    }

    &:focus-visible {
      outline: 2px solid var(--primary-community, #3b82f6);
      outline-offset: 1px;
    }
  }

  // Already part of this community: still listed so a search finds it, but not selectable
  .add-actor-to-community__row--added {
    background: #f9fafb;
    cursor: default;

    &:hover {
      border-color: #e5e7eb;
      box-shadow: none;
    }
  }

  .add-actor-to-community__badge {
    padding: 3px 8px;
    border-radius: 999px;
    background: #eef2f7;
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: #6b7280;
    white-space: nowrap;
  }

  .add-actor-to-community__row--selected {
    border-color: var(--primary-community, #3b82f6);
    background: rgba(59, 130, 246, 0.04);
  }

  .add-actor-to-community__logo {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    border: 1px solid #f3f4f6;
    background-color: #f9fafb;
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.7rem;
    font-weight: 600;
    color: #6b7280;
  }

  .add-actor-to-community__check {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 1px solid #d1d5db;
    color: white;
  }

  .add-actor-to-community__check--on {
    border-color: var(--primary-community, #3b82f6);
    background: var(--primary-community, #3b82f6);
  }

  .add-actor-to-community__chip {
    max-width: 220px;
    padding: 3px 4px 3px 10px;
    border-radius: 999px;
    background: #f3f4f6;
    font-size: 0.7rem;
    font-weight: 600;
    color: #374151;
  }

  .add-actor-to-community__chip-remove {
    border: 0;
    background: transparent;
    color: #6b7280;
    font-size: 0.95rem;
    line-height: 1;
    cursor: pointer;
    padding: 0 4px;

    &:hover {
      color: #111827;
    }
  }

  .add-actor-to-community__create {
    border: 0;
    background: transparent;
    padding: 0;
    font-size: 0.78rem;
    font-weight: 600;
    color: #6b7280;
    text-decoration: underline;
    cursor: pointer;

    &:hover {
      color: var(--primary-community, #3b82f6);
    }
  }
</style>
